/**
 * api/activities.ts —— 日常打卡（§五 5.7）
 *
 * ⚠ **没有「活动类型」**（2026-08-20 删）：没有 `type` 入参、没有 `?type` 查询、
 *   `ActivityStats` 里也没有 `byType`。看到「快报好像要分类」**不要补回来**。
 * ⚠ 人次（`pax`）与两个时间窗的统计**全部由服务端算好下发**，前端不许自己 reduce（红线 4）。
 * ⚠ `photos[].objectKey` **不是可访问地址**：COS 私有读，读图把它拼进 `api/upload.ts` 的 `imgUrl()`。
 */
import { del, get, post, put, type ReqOptions } from './client.js';
import type { Activity, ActivityListResult, ActivityStats, Guest } from '@shared-types';

/** 时间段三档（一期已定：**不做日期区间选择器**，区间留给导出与快报） */
export type SpanKey = '14' | 'cycle' | 'all';

export interface ActivityListQuery {
  /** 空 = 全营；⚠ 全营时服务端**不返回归档队**的记录，显式选中归档队才能查它的历史 */
  teamId?: string;
  span?: SpanKey;
  q?: string;
}

function qs(q: ActivityListQuery): string {
  const p = new URLSearchParams();
  if (q.teamId) p.set('teamId', q.teamId);
  if (q.span) p.set('span', q.span);
  if (q.q) p.set('q', q.q);
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** 列表。⚠ 出参多带 `pax` 与 `visible` 两个数（§五 5.7 补充 E1），工具条那行直接用 */
export const listActivities = (q: ActivityListQuery = {}, opts?: ReqOptions) =>
  get<ActivityListResult>(`/api/activities${qs(q)}`, opts);

/**
 * 统计。⚠⚠ **同时返回两个口径**：`recent14`（近 14 天）与 `inPeriod`（当前周期内）。
 * **两个数字不一样是正确的**——它们是同一份数据的两个时间窗，谁都不要去"对齐"。
 */
export const activityStats = (teamId?: string, opts?: ReqOptions) =>
  get<ActivityStats>(`/api/activities/stats${teamId ? `?teamId=${encodeURIComponent(teamId)}` : ''}`, opts);

export const getActivity = (id: string, opts?: ReqOptions) =>
  get<Activity>(`/api/activities/${encodeURIComponent(id)}`, opts);

export interface ActivitySaveInput {
  teamId: string;
  date: string;
  summary: string;
  memberIds: string[];
  guests: Guest[];
  /** 由 `api/upload.ts` 签发的对象键；⚠ 1—9 张 */
  photoKeys: string[];
  cover: number;
}

/** 新增（本队）。⚠ `createdBy` 由服务端取当前登录人，**不接受传入**——它是维护权限的依据 */
export const createActivity = (input: ActivitySaveInput) => post<Activity>('/api/activities', input);

/**
 * 编辑（创建人 / 队长 / 管理员）。
 * ⚠ **不能换队**：换队会串统计，服务端会拒；要换只能删了重建。
 */
export const updateActivity = (id: string, input: ActivitySaveInput) =>
  put<Activity>(`/api/activities/${encodeURIComponent(id)}`, input);

/** 删除。⚠ 统计（act14 / pax / last）是现算的，删完自动回退，前端不需要"扣减"任何数字 */
export const deleteActivity = (id: string) => del<{ ok: true }>(`/api/activities/${encodeURIComponent(id)}`);
